import React, { Fragment } from 'react'
import { Flex, Icon } from '.'
import { get } from 'lodash'
import { Checkbox as BPCheckbox, Classes, Collapse } from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import Button from './buttons/Button'
import { ISelection } from '@hooks/useSelection'
import { IRecord, Order, IPageInfo } from '@api/types'
import numeral from 'numeral'
import { useSelection, useIntersect } from '@hooks'
import { Spinner } from '@ui'

export interface IColumn {
  label: React.ReactNode
  render: (item: any) => React.ReactNode
  renderEdit?: (item: any) => React.ReactNode
  sort?: string
  onClick?: (item: any) => any
  style?: any
  tooltip?: string
}
export interface ISort {
  column: string
  order: Order
  isSorting?: boolean
  onChange: (column: string) => void
}
export interface IPage extends IPageInfo {
  next: () => void
  total?: number
  isPaging?: boolean
}

export interface ITableRecord {
  onClick?: (row: any) => void
  children?: React.ReactNode
  childPaddingLeft?: number
}

interface ITableProps {
  columns: IColumn[]
  data: any[]
  isLoading?: boolean
  amountSkeletonRows?: number
  condensed?: boolean
  page?: IPage
  sort?: ISort
  selection?: ISelection
  prependHeader?: React.ReactNode
  appendBody?: React.ReactNode
  trackSelectAll?: () => void
  trackSelect?: () => void
  stickyHeader?: boolean
  onRowEnter?: (id: string) => void
  onRowLeave?: () => void
  className?: string
  editingId?: string | undefined
  noDataMessage?: string
  headerSelectionCheckboxTooltip?: string
  headerTop?: number
}

const Checkbox = ({
  onChange,
  checked,
  header,
  rightElement,
  label,
  tooltip,
}: {
  onChange: () => void
  checked: boolean
  header: boolean
  rightElement?: React.ReactNode
  label?: string
  tooltip?: string
}) => {
  if (header) {
    return (
      <th style={{ border: 'none' }}>
        <Tooltip2 content={tooltip} disabled={!tooltip} position="right">
          <BPCheckbox aria-label={label} {...{ onChange, checked }} />
        </Tooltip2>
      </th>
    )
  }

  return (
    <td className="table--checkbox">
      <Flex align="center">
        <Tooltip2 content={tooltip} disabled={!tooltip} position="right">
          <BPCheckbox aria-label={label} {...{ onChange, checked }} />
        </Tooltip2>
        {rightElement}
      </Flex>
    </td>
  )
}

const SortArrow = ({ sort, column }: { sort?: ISort; column: IColumn }) => {
  if (!sort || !column.sort || sort.column !== column.sort) return null
  return <Icon name={sort.order === 'DESC' ? 'long-arrow-down' : 'long-arrow-up'} />
}

const TH = ({ column, sort, headerTop }: { column: IColumn; sort?: ISort; headerTop?: number }) => {
  const sortable = sort && column.sort
  const active = sort && sort.column === column.sort
  const sorting = active && sort && sort.isSorting
  return (
    <th
      style={{ ...column.style, top: headerTop, background: 'var(--white)' }}
      className={`${sortable ? 'sortable' : ''}${active ? ' active' : ''}`}
      onClick={() => sort && column.sort && sort.onChange(column.sort)}>
      {column.tooltip ? <Tooltip2 content={column.tooltip}>{column.label}</Tooltip2> : column.label}
      <SortArrow {...{ sort, column }} />
      {sorting && <Spinner size={12} />}
    </th>
  )
}

function Footer({ data, page }: { data: IRecord[]; page?: IPage }) {
  if (!page || !page.hasNextPage) return null
  return (
    <div className="table__footer">
      {page.total ? (
        <>
          Showing {data.length}/{numeral(page.total).format('0,0')}
        </>
      ) : null}
      <Button onClick={page.next} loading={page && page.isPaging} text="Load More" />
    </div>
  )
}

export default function Table({
  appendBody = null,
  prependHeader = null,
  columns,
  data,
  editingId,
  isLoading,
  amountSkeletonRows,
  condensed,
  page,
  selection,
  sort,
  trackSelect,
  trackSelectAll,
  stickyHeader = true,
  onRowEnter,
  onRowLeave,
  className,
  noDataMessage,
  headerSelectionCheckboxTooltip = '',
  headerTop = -1,
}: ITableProps) {
  const { refItem } = useIntersect({
    threshold: [0.75],
    callback: page && page.hasNextPage && !page.isPaging && page.next ? page.next : undefined,
  })

  const expanded = useSelection()

  const onChevronClick = (clickedId: string) => expanded.toggle(clickedId)

  if (isLoading)
    return <TableLoading {...{ columns, selection, condensed, rows: amountSkeletonRows }} />

  const isNoData = !isLoading && !data.length
  const allSelected = data.length ? get(selection, 'count') === data.length : false

  const toggleAll = () => {
    if (!selection) return
    if (allSelected) return selection.clear()
    selection.addAll(data.map(({ id }) => id))
    if (trackSelectAll) trackSelectAll()
  }

  const hasChildren = data.reduce((result, curr) => {
    return result || !!curr.children
  }, false)

  return (
    <>
      <table
        className={[
          'table',
          stickyHeader ? '' : 'not-sticky',
          condensed ? 'condensed' : '',
          className,
        ].join(' ')}>
        <thead>
          {prependHeader}
          <tr>
            {selection && (
              <Checkbox
                checked={allSelected}
                onChange={toggleAll}
                header
                label="select_all"
                tooltip={headerSelectionCheckboxTooltip}
              />
            )}
            {hasChildren && <th style={{ width: 13 }} />}
            {columns.map((column, index) => (
              <TH key={`th-${column.label}`} {...{ column, sort, headerTop }} />
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((d, i) => {
            let className = ''
            if (selection && selection.items[d.id]) className = className + `${className} selected`
            if (d.onClick && !d.children) className = `${className} hoverable`
            return (
              <Fragment key={i}>
                <tr
                  key={`tr-${d.id}`}
                  className={className}
                  onClick={d.onClick}
                  onMouseEnter={() => {
                    if (d.id) onRowEnter?.(d.id)
                  }}
                  onMouseLeave={onRowLeave}
                  ref={
                    (data.length > 10 && i === data.length - 8
                      ? refItem
                      : null) as React.Ref<HTMLTableRowElement>
                  }>
                  {selection && (
                    <Checkbox
                      header={false}
                      checked={selection.items[d.id]}
                      onChange={() => {
                        selection.toggle(d.id)
                        if (!selection.items[d.id] && trackSelect) {
                          trackSelect()
                        }
                      }}
                      label={d.name}
                    />
                  )}
                  {hasChildren &&
                    (d.children ? (
                      <td onClick={() => onChevronClick(d.id)}>
                        <Icon
                          style={{ marginLeft: 5, width: 13 }}
                          name={expanded.items[d.id] ? 'chevron-down' : 'chevron-right'}
                        />
                      </td>
                    ) : (
                      <td />
                    ))}
                  {columns.map((c, i) => {
                    /* eslint-disable @typescript-eslint/ban-ts-comment */
                    // @ts-ignore
                    const onClick = c.onClick ? () => c.onClick(d) : undefined
                    return (
                      <td style={c.style} onClick={onClick} key={`td-${d.id}-${c.label}`}>
                        {d.id === editingId && c.renderEdit ? c.renderEdit(d) : c.render(d)}
                      </td>
                    )
                  })}
                </tr>
                {d.children && (
                  <tr key={`${d.id}-child`} style={{ border: 'none' }}>
                    <td
                      style={{ padding: 0 }}
                      colSpan={selection ? columns.length + 2 : columns.length + 1}>
                      <div
                        style={{
                          paddingLeft: d.childPaddingLeft,
                        }}>
                        <Collapse isOpen={expanded.items[d.id]} keepChildrenMounted>
                          {d.children}
                        </Collapse>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
          {isNoData && <TREmpty message={noDataMessage} />}
          {appendBody}
        </tbody>
      </table>
      <Footer {...{ data, page }} />
    </>
  )
}

const TREmpty = ({ message }: { message?: string }) => (
  <tr>
    {/*
    // @ts-ignore */}
    <td colSpan="100%" style={{ textAlign: 'center' }}>
      <Flex align="center" justify="center" style={{ height: 140 }}>
        <Icon name="exclamation-triangle" style={{ marginRight: 10 }} />
        {message ? message : 'No Data'}
      </Flex>
    </td>
  </tr>
)

interface ILoadingProps {
  condensed?: boolean
  selection?: ISelection
  columns: IColumn[]
  rows?: number
}
export function TableLoading({ columns, selection, condensed, rows = 9 }: ILoadingProps) {
  const cell = <span className={Classes.SKELETON}>Placeholder text</span>
  const checkbox = selection && <Checkbox header={false} checked={false} onChange={() => null} />

  return (
    <table className={['table', 'not-sticky', condensed ? 'condensed' : ''].join(' ')}>
      <thead>
        <tr>
          {selection && <Checkbox checked={false} onChange={() => null} header />}
          {columns.map((column, i) => (
            <TH key={i} column={column} />
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }).map((_, i) => (
          <tr key={i}>
            {checkbox}
            {columns.map((c, i) => (
              <td key={i}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}
